import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, concatMap, filter, from, map, of, startWith, tap } from 'rxjs';

import { WorkspaceActions } from './workspace.actions';
import { selectSelectedOrganizationId, selectSelectedProjectId, selectStatus } from './workspace.selectors';
import { WorkspaceLoaderService } from './workspace-loader.service';

const SELECTION_KEY_PROJECT = 'selectedProjectId';
const SELECTION_KEY_ORG = 'selectedOrganizationId';

/** Single load pipeline — init and reload share one exhaustMap so they never run concurrently. */
export const workspaceLoad$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), loaderService = inject(WorkspaceLoaderService)) =>
    actions$.pipe(
      ofType(WorkspaceActions.init, WorkspaceActions.reload),
      concatLatestFrom(() => store.select(selectStatus)),
      filter(([action, status]) => {
        if (action.type === WorkspaceActions.init.type) {
          return status === 'idle' || status === 'error';
        }
        return true;
      }),
      concatMap(() =>
        from(loaderService.load()).pipe(
          map((payload) => WorkspaceActions.loadSucceeded(payload)),
          catchError((err) => of(WorkspaceActions.loadFailed({ error: String(err?.message ?? err) }))),
          startWith(WorkspaceActions.loadStarted())
        )
      )
    ),
  { functional: true }
);

/** Restore the last-selected project/org from localStorage when the app boots. */
const workspaceRestoreSelection$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(WorkspaceActions.init),
      map(() => {
        let projectId: string | null = null;
        let organizationId: string | null = null;
        try {
          projectId = localStorage.getItem(SELECTION_KEY_PROJECT);
          organizationId = localStorage.getItem(SELECTION_KEY_ORG);
        } catch {}
        return WorkspaceActions.selectionRestored({ projectId, organizationId });
      })
    ),
  { functional: true }
);

/** Persist selection whenever it changes (single place that touches localStorage). */
const workspacePersistSelection$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(WorkspaceActions.selectProject, WorkspaceActions.loadSucceeded),
      concatLatestFrom(() => [store.select(selectSelectedProjectId), store.select(selectSelectedOrganizationId)]),
      tap(([, projectId, organizationId]) => {
        try {
          if (projectId) localStorage.setItem(SELECTION_KEY_PROJECT, projectId);
          else localStorage.removeItem(SELECTION_KEY_PROJECT);
          if (organizationId) localStorage.setItem(SELECTION_KEY_ORG, organizationId);
          else localStorage.removeItem(SELECTION_KEY_ORG);
        } catch {}
      })
    ),
  { functional: true, dispatch: false }
);

export const workspaceEffects = {
  workspaceLoad$,
  workspaceRestoreSelection$,
  workspacePersistSelection$,
};

/** @deprecated Use workspaceLoad$ — kept for existing unit tests. */
const workspaceInit$ = workspaceLoad$;
